import React, { useCallback, useRef, type FC, type ReactNode } from "react";
import { type To } from "react-router-dom";
import { ArrowLeftIcon, MenuIcon } from "@omega/icons";
import { Svg } from "@shared/ui/svg";
import { AppPaths } from "@app/router/paths";
import { runAppBack } from "@app/back";
import { useWindows, type OverlayHandle } from "@app/overlay";
import { searchObjectWindows } from "@src/pages/SearchObject/searchObject.windows";
import { MobileNavButton } from "./MobileNavButton";
import "./MobileNavBar.scss";

/** Пункт нижней навигации shell. */
export interface MobileNavBarOption {
    name: string;
    icon: ReactNode;
    linkTo?: To;
    onClick?: () => void;
}

/**
 * Нижняя навигация мобильного shell: меню, сканер, назад.
 *
 * Контракт shell ↔ SearchObject (`app.searchObject`):
 * - `MobileNavBar` — opener окна сканера, но живёт в `MobileAppShell` и **не**
 *   размонтируется при смене route (`<Outlet />` меняется под ним).
 * - Поэтому дефолтный `ownerLifecycle: "close-on-owner-unmount"` **не** закрывает
 *   сканер при переходе в «Меню»: owner остаётся смонтированным.
 * - Overlay stack сам по себе не слушает URL — route-close здесь нет.
 * - Локальный контракт: shell-навигация («Меню») явно закрывает сканер через
 *   сохранённый `OverlayHandle`. Это не общий route-policy, а поведение shell.
 */
export const MobileNavBar: FC = () => {
    const windows = useWindows();
    const searchObjectHandleRef = useRef<OverlayHandle | null>(null);

    const openSearchObject = useCallback(() => {
        searchObjectHandleRef.current = searchObjectWindows.main.open(
            windows,
            {},
            {
                onClosed: () => {
                    searchObjectHandleRef.current = null;
                },
            },
        );
    }, [windows]);

    /** Shell уходит с текущего экрана — сканер больше не актуален, закрываем явно. */
    const closeSearchObjectOnShellNav = useCallback(() => {
        searchObjectHandleRef.current?.close();
        searchObjectHandleRef.current = null;
    }, []);

    const navOptions: MobileNavBarOption[] = [
        {
            name: "Меню",
            icon: <MenuIcon size={24} />,
            linkTo: AppPaths.MENU,
            onClick: closeSearchObjectOnShellNav,
        },
        {
            name: "Сканер",
            icon: <Svg src="/images/navbar/scanner.svg" />,
            onClick: openSearchObject,
        },
        {
            name: "Назад",
            icon: <ArrowLeftIcon size={24} />,
            onClick: () => {
                runAppBack();
            },
        },
    ];

    return (
        <div className="mobile_nav_bar">
            {navOptions.map((option) => (
                <MobileNavButton key={option.name} navOption={option} />
            ))}
        </div>
    );
};
